/**
 * Stripe Service
 * Handles subscription creation and customer portal via Cloud Functions.
 * Subscription state is read from Firestore (not a client SDK).
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { highestEntitlement, knownEntitlements } from '../constants/entitlements';
import type { CustomerSubscriptionInfo, PricePoint } from '../types';

/**
 * Build the list of price points from environment variables.
 * Expects VITE_STRIPE_MONTHLY_PRICE_ID_4 through _12.
 */
export const getPricePoints = (): PricePoint[] => {
  const points: PricePoint[] = [];
  for (let amount = 4; amount <= 12; amount++) {
    const priceId = import.meta.env[`VITE_STRIPE_MONTHLY_PRICE_ID_${amount}`];
    if (priceId) {
      points.push({ amount, priceId });
    }
  }
  return points;
};

/**
 * Map a Firestore subscription document to CustomerSubscriptionInfo.
 */
export const mapSubscriptionData = (
  data: Record<string, unknown> | null | undefined
): CustomerSubscriptionInfo => {
  const now = new Date().toISOString();

  if (!data) {
    return {
      status: 'free',
      activeEntitlementIds: [],
      hasActiveSubscription: false,
      lastUpdated: now,
    };
  }

  const firestoreStatus = data.status as string | undefined;
  const role = data.role as string | undefined;
  const isActive = firestoreStatus === 'active' || firestoreStatus === 'trialing';

  // The Stripe `role` carries an entitlement key. Resolve it through the SoT
  // instead of comparing to a 'data_sync' literal — that literal mapped every
  // other key, `spending_tools` included, to 'free'.
  const entitlementIds = isActive ? knownEntitlements([role ?? '']) : [];
  const level = highestEntitlement(entitlementIds);

  const currentPeriodEnd = data.currentPeriodEnd as number | undefined;
  const expirationDate = currentPeriodEnd
    ? new Date(currentPeriodEnd * 1000).toISOString()
    : undefined;

  return {
    status: level ?? 'free',
    activeEntitlementIds: entitlementIds,
    hasActiveSubscription: isActive,
    activeProductId: data.priceId as string | undefined,
    expirationDate,
    willRenew: data.cancelAtPeriodEnd === true ? false : isActive ? true : undefined,
    monthlyAmount: data.monthlyAmount as number | undefined,
    lastUpdated: now,
  };
};

/**
 * Create a Stripe subscription via Cloud Function.
 * Returns the subscription ID and client secret for payment confirmation.
 *
 * `opts.requestTrial` asks the server to apply the paid-launch trial
 * (`trial_period_days=30`, PAID_LAUNCH_PLAN.md §4b, card T2) — the server
 * only honors it when its own `freeTrialUsed` check passes, and reports back
 * whether it actually did via `trialApplied`. Callers should always render
 * off `trialApplied` (not the fact that they asked), since the server can
 * decline even when the client thought the trial was available.
 */
export const createSubscription = async (
  priceId: string,
  promotionCode?: string,
  opts?: { requestTrial?: boolean },
): Promise<{ subscriptionId: string; clientSecret: string | null; autoActivated?: boolean; trialApplied?: boolean }> => {
  const fn = httpsCallable<
    { priceId: string; promotionCode?: string; requestTrial?: boolean },
    { subscriptionId: string; clientSecret: string | null; autoActivated?: boolean; trialApplied?: boolean }
  >(functions, 'enough_create_stripe_subscription');

  const result = await fn({
    priceId,
    ...(promotionCode ? { promotionCode } : {}),
    ...(opts?.requestTrial ? { requestTrial: true } : {}),
  });
  return result.data;
};

/**
 * Create a Stripe Customer Portal session via Cloud Function.
 * Returns the portal URL to redirect the user to.
 */
export const createPortalSession = async (): Promise<string> => {
  const fn = httpsCallable<Record<string, never>, { url: string }>(
    functions,
    'enough_create_stripe_portal_session'
  );

  const result = await fn({});
  return result.data.url;
};
